<template>
  <div>
    <v-dialog
      :width="width"
      scrollable
      v-model="showDialog">
      <!-- Allow the usual Vuetify activator syntax from outside -->
      <template #activator="{ on, attrs }">
        <slot name="activator" :on="on" :attrs="attrs"/>
      </template>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-toolbar>
        <v-card-title>
          <v-btn color="primary" @click="selectAll">
            {{ $t('select_all' )}}
          </v-btn>
          <v-btn class="ml-2" @click="clear">
            {{ $t('clear') }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item-group
              v-model="selectedCustomerIds"
              active-class="selected-customer"
              multiple>
              <template v-for="customer in selectableCustomers">
                <v-list-item :value="customer.id" :key="customer.id">
                  <template #default="{ active }">
                    <v-list-item-action>
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title :class="{'ml-4': !customer.isTopLevel()}">
                        <v-icon v-if="customer.isTopLevel()">mdi-office-building</v-icon>
                        <v-icon v-else>mdi-garage</v-icon>
                        <span class="ml-1">{{ customer.name }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </template>
            </v-list-item-group>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <div>
            <div v-for="(error, index) in customerErrors" :key="index" class="danger--text">
              {{ error }}
            </div>
          </div>
          <v-spacer/>
          <v-btn @click="hide" class="mr-2">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" @click="handleSelection">{{ $t('ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CustomerRecord from '@/orm/models/CustomerRecord'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import { each, map, isEmpty, find, includes, filter } from 'lodash'

export default {
  name: 'CustomerMultiSelect',
  mixins: [
    CurrentUserMixin,
    ValidationMixin
  ],
  props: {
    // The title of the dialog
    title: {
      type: String
    },
    // The width of the v-dialog component
    width: {
      type: String,
      default: '600'
    },
    // Default binding to the list of customers to select. We convert this to a list of customer ids internally,
    // then later emit an 'input' event with the selected customer objects.
    value: {
      type: Array,
      default: () => []
    }
  },
  validations: {
    // Require at least one selected customer.
    selectedCustomerIds: {
      required: (value, vm) => {
        return !isEmpty(value)
      }
    }
  },
  data () {
    return {
      showDialog: false,
      selectedCustomerIds: []
    }
  },
  methods: {
    /**
     * Hide the modal dialog
     */
    hide () {
      this.showDialog = false
    },
    /**
     * Select all available highest-level customers.
     * (Skip any customers that are children of other selectable customers, as it would be redundant to include them.)
     */
    selectAll () {
      const selectedCustomerIds = []
      const topLevelCustomerIds = map(filter(this.selectableCustomers, x => x.parent_id == null), x => x.id)
      each(this.selectableCustomers, customer => {
        if (!includes(topLevelCustomerIds, customer.parent_id)) {
          selectedCustomerIds.push(customer.id)
        }
      })
      this.selectedCustomerIds = selectedCustomerIds
    },
    /**
     * Clear the customer selection.
     */
    clear () {
      this.selectedCustomerIds = []
    },
    /**
     * Handle updating the customer selection and emitting an event when the 'OK' button is pressed.
     */
    handleSelection (event) {
      this.$v.$touch()
      if (this.$v.$invalid) {
        // Failed validation, prevent modal from closing
        event.preventDefault()
        return false
      }
      const selectedCustomers = []
      each(this.selectedCustomerIds, customerId => {
        const customer = find(this.selectableCustomers, x => x.id === customerId)
        if (customer) {
          selectedCustomers.push(customer)
        }
      })
      this.$emit('input', selectedCustomers)
      this.hide()
    }
  },
  computed: {
    selectableCustomers () {
      return CustomerRecord.sortCustomersHierarchically(this.customers)
    },
    customerErrors () {
      return this.validationErrors([{
        prop: this.$v.selectedCustomerIds,
        message: this.$t('please_select_at_least_one')
      }])
    }
  },
  watch: {
    value (newValue) {
      this.selectedCustomerIds = map(newValue, x => x.id)
    }
  },
  mounted () {
    this.selectedCustomerIds = map(this.value, x => x.id)
  }
}
</script>

<style scoped>
.selected-customer {
  background-color: #ccddff;
}
</style>
